import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const TRACKING_ACTIVE_STATUSES = new Set(["accepted", "picking_up", "picked_up", "delivering"]);

// Phase 43.33 — Tier 1 PWA tracking
// - Local GPS via watchPosition (alta frequenza, no costo)
// - Throttle POST al backend: max 1 ogni 12s
// - Throttle anche per spostamento: ignora ping se < 8 metri dall'ultimo POST
// - Wake Lock per tenere schermo acceso durante consegna attiva
// - ZERO chiamate Google (Directions/Geocoding) per tick
const POST_MIN_INTERVAL_MS = 12_000;
const POST_MIN_DISTANCE_M = 8;
const RETRY_INTERVAL_MS = 30_000; // se l'ultimo POST è fallito, riprova al massimo dopo 30s

function _friendlyGeoError(err) {
  const code = err && typeof err.code === "number" ? err.code : null;
  if (code === 1) {
    return {
      code: "permission_denied",
      message: "Hai negato l'accesso alla posizione. Per condividere il tracking live con il cliente, abilita la geolocalizzazione dalle impostazioni del browser per questo sito.",
    };
  }
  if (code === 2) {
    return {
      code: "position_unavailable",
      message: "Posizione GPS non disponibile in questo momento. Controlla di avere il segnale GPS attivo.",
    };
  }
  if (code === 3) {
    return {
      code: "timeout",
      message: "Timeout sulla lettura GPS. Riproviamo automaticamente al prossimo update.",
    };
  }
  return { code: "unknown", message: err?.message || "Errore GPS sconosciuto." };
}

// Haversine in metri (no Google) — usato solo per il throttle locale
function _distanceMeters(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * useDriverLocationBroadcaster — when an order is in a tracking-active state,
 *   - acquires a Screen Wake Lock (where supported) to keep the screen on while driving
 *   - subscribes to `watchPosition` (high accuracy, very frequent local updates)
 *   - throttles network POSTs to the backend: at most once every 12 s AND only if
 *     the device has moved ≥ 8 m since the last successful POST
 *   - releases everything when the order leaves the active states or component unmounts
 *
 * Importantly: this hook never calls Google APIs. All geometry (distance, throttle)
 * is computed locally; the backend handles persistence.
 *
 * Returns { active, lastPing, lastCoords, error, errorCode, supported, retry, wakeLockHeld }.
 */
export function useDriverLocationBroadcaster({ orderId, status }) {
  const [lastPing, setLastPing] = useState(null);
  const [lastCoords, setLastCoords] = useState(null);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [wakeLockHeld, setWakeLockHeld] = useState(false);

  const supported = typeof navigator !== "undefined" && !!navigator.geolocation;
  const active = !!orderId && TRACKING_ACTIVE_STATUSES.has(status) && supported;

  const watchIdRef = useRef(null);
  const wakeLockRef = useRef(null);
  const inFlightRef = useRef(false);
  const lastPostAtRef = useRef(0);
  const lastPostedCoordsRef = useRef(null);
  const retryTickRef = useRef(0);

  // -------- Wake Lock helpers --------
  const acquireWakeLock = useCallback(async () => {
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return;
    try {
      const wl = await navigator.wakeLock.request("screen");
      wakeLockRef.current = wl;
      setWakeLockHeld(true);
      wl.addEventListener("release", () => {
        setWakeLockHeld(false);
        wakeLockRef.current = null;
      });
    } catch (e) {
      // Wake Lock può fallire se la tab non è visibile; non è critico
      setWakeLockHeld(false);
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try { await wakeLockRef.current.release(); } catch (e) { /* ignore */ }
      wakeLockRef.current = null;
      setWakeLockHeld(false);
    }
  }, []);

  // Riacquisisce il Wake Lock quando la tab torna visibile (iOS lo rilascia in background)
  useEffect(() => {
    if (!active) return undefined;
    const onVis = () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current) {
        acquireWakeLock();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [active, acquireWakeLock]);

  // -------- POST throttled --------
  const maybePost = useCallback(async (coords) => {
    if (inFlightRef.current) return;
    if (!orderId) return;
    const now = Date.now();
    const elapsed = now - lastPostAtRef.current;
    const dist = _distanceMeters(lastPostedCoordsRef.current, coords);
    // Salta se troppo presto E spostamento minimo (a meno che ultimo POST fallito da un po')
    const hadRecentFailure = errorCode === "network" && elapsed < RETRY_INTERVAL_MS;
    if (elapsed < POST_MIN_INTERVAL_MS && dist < POST_MIN_DISTANCE_M && !hadRecentFailure) {
      return;
    }
    inFlightRef.current = true;
    try {
      const ping = {
        lat: coords.lat,
        lng: coords.lng,
        heading: coords.heading ?? null,
        speed_kmh: coords.speed_kmh ?? null,
        accuracy_m: coords.accuracy_m ?? null,
      };
      await api.post(`/orders/${orderId}/driver-location`, ping);
      lastPostAtRef.current = Date.now();
      lastPostedCoordsRef.current = { lat: coords.lat, lng: coords.lng };
      setLastPing(new Date());
      setError(null);
      setErrorCode(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setErrorCode("network");
    } finally {
      inFlightRef.current = false;
    }
  }, [orderId, errorCode]);

  // -------- watchPosition lifecycle --------
  useEffect(() => {
    if (!active) {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      releaseWakeLock();
      return undefined;
    }

    acquireWakeLock();

    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          heading: Number.isFinite(pos.coords.heading) ? pos.coords.heading : null,
          speed_kmh: Number.isFinite(pos.coords.speed) ? Math.max(0, pos.coords.speed) * 3.6 : null,
          accuracy_m: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
          at: Date.now(),
        };
        setLastCoords(coords);  // ← marker live aggiornato istantaneamente
        // POST throttled (fire-and-forget)
        maybePost(coords);
      },
      (geoErr) => {
        const friendly = _friendlyGeoError(geoErr);
        setError(friendly.message);
        setErrorCode(friendly.code);
      },
      { enableHighAccuracy: true, maximumAge: 2_000, timeout: 15_000 },
    );
    watchIdRef.current = id;

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      releaseWakeLock();
    };
    // retryTickRef purposely included to allow manual retry
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, maybePost, acquireWakeLock, releaseWakeLock, retryTickRef.current]);

  const retry = useCallback(() => {
    setError(null);
    setErrorCode(null);
    retryTickRef.current += 1;
    // Forza next POST a riprovare anche se throttle aperto
    lastPostAtRef.current = 0;
  }, []);

  return { active, lastPing, lastCoords, error, errorCode, supported, retry, wakeLockHeld };
}

export { TRACKING_ACTIVE_STATUSES };
